import React, { useMemo, useEffect, useRef } from 'react';
import './SummaryBackside.css'; // Import the updated CSS for styling
import Rotate90DegreesCcwIcon from '@mui/icons-material/Rotate90DegreesCcw'; // Import the Flip Back icon
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { getUserId } from '../utils/userId'; // Import getUserId
import { todayPuzzleId, shiftPuzzleId } from '../utils/puzzleId';
import { buildShareText } from '../utils/shareText';
import NextPuzzleCountdown from './summary/NextPuzzleCountdown';
import ShareCard from './summary/ShareCard';
import SecondaryActions from './summary/SecondaryActions';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { useState } from 'react';

// Rows shown under the histogram once the full list is too long to render.
const PODIUM_SIZE = 5;

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const SummaryBackside = ({
  setIsFlipped, setIsPaused, isComplete, performanceData, totalTime, formatTime,
  onRename,
  userPuzzleHistory,
  // Phase 4
  puzzleId, grid, assistedKeys, assistCount = 0, isArchive = false, gaveUp = false,
  canInstall, onInstall, canNotify, onNotify,
  canPlayYesterday, onPlayYesterday,
}) => {
  const [open, setOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success'); // 'success' or 'error'
  
  // Add new state for feedback functionality
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  const handleFlipBack = () => {
    setIsFlipped(false);
    if (!isComplete) {
      setIsPaused(false);
    }
  };


  // Add feedback handlers
  const handleFeedbackClick = () => {
    setShowFeedback(!showFeedback);
    if (!showFeedback) {
      setFeedbackText('');
    }
  };

  const handleFeedbackSubmit = async () => {
    // Check if feedback is at least 2 characters
    if (feedbackText.trim().length < 1) {

      setOpen(true);
      return;
    }

    setIsSubmitting(true);
    
    try {
      const userId = getUserId();
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
      
      const response = await fetch(`${backendUrl}/api/logFeedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId: userId,
          feedback: feedbackText.trim()
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSnackbarMessage('Thank you for your feedback!');
        setSnackbarSeverity('success');
        setShowFeedback(false);
        setFeedbackText('');
      } else {
        setSnackbarMessage(data.error || 'Failed to submit feedback');
        setSnackbarSeverity('error');
      }
    } catch (error) {
      setSnackbarMessage('Failed to submit feedback. Please try again.');
      setSnackbarSeverity('error');
      console.error('Error submitting feedback:', error);
    } finally {
      setIsSubmitting(false);
      setOpen(true);
    }
  };

  const handleFeedbackCancel = () => {
    setShowFeedback(false);
    setFeedbackText('');
  };

  // Helper function to get ordinal suffix
  const getOrdinalSuffix = (position) => {
    if (typeof position !== 'number') return position;
    const j = position % 10,
          k = position % 100;
    if (j === 1 && k !== 11) {
      return position + "st";
    }
    if (j === 2 && k !== 12) {
      return position + "nd";
    }
    if (j === 3 && k !== 13) {
      return position + "rd";
    }
    return position + "th";
  };

  // Calculate average completion time and streak
  const { averageCompletionTime, currentStreak } = useMemo(() => {
    if (!userPuzzleHistory?.userPuzzleHistory || userPuzzleHistory.userPuzzleHistory.length === 0) {
      return { averageCompletionTime: null, currentStreak: 0 };
    }

    // Two kinds of row are excluded from both the average and the streak:
    //
    //  - give-ups (`gave_up`), which count as "played" but not as "solved";
    //  - archive replays (`context === 'archive'`), because a leisurely run at
    //    an old puzzle is not a daily solve, and backfilling a streak from the
    //    archive would make the streak meaningless.
    //
    // Both fields are absent on rows written before their columns existed;
    // those are treated as genuine daily solves.
    const solved = userPuzzleHistory.userPuzzleHistory.filter(
      entry => !entry.gave_up && entry.context !== 'archive'
    );

    // completion_time comes back as TEXT from Postgres, so coerce before summing
    // (a bare reduce would concatenate strings).
    const completionTimes = solved
      .map(entry => Number(entry.completion_time))
      .filter(time => Number.isFinite(time));

    const averageSeconds = completionTimes.length > 0
      ? completionTimes.reduce((sum, time) => sum + time, 0) / completionTimes.length
      : null;

    const averageFormatted = averageSeconds != null ? formatTime(Math.round(averageSeconds)) : null;

    // Streak. `puzzle_id` is TEXT in the database, so the lookup set must be
    // string-keyed — comparing it against a computed Number silently never
    // matched, which is why the streak always read zero.
    const solvedPuzzleIds = new Set(solved.map(entry => String(entry.puzzle_id)));

    // Count back from today, or from yesterday if today isn't solved yet —
    // otherwise a player mid-solve sees a long streak reported as 0.
    const todayId = todayPuzzleId();
    let cursor = solvedPuzzleIds.has(String(todayId)) ? todayId : shiftPuzzleId(todayId, -1);

    let streak = 0;
    while (cursor != null && solvedPuzzleIds.has(String(cursor))) {
      streak++;
      cursor = shiftPuzzleId(cursor, -1);
    }

    return { averageCompletionTime: averageFormatted, currentStreak: streak };
  }, [userPuzzleHistory, formatTime]);

  // Compute histogram data
  const bucketSize = 10; // Updated bucket size to 10 seconds
  const maxTime = 300; // 5 minutes in seconds
  const numBuckets = Math.ceil(maxTime / bucketSize); // 30 buckets
  const bins = Array(numBuckets + 1).fill(0); // 31 bins, last one for >5 minutes

  performanceData.forEach(entry => {
    const completionTime = entry.completion_time;
    let bucket = Math.floor(completionTime / bucketSize);
    if(bucket >= numBuckets){
      bucket = numBuckets; // last bucket for >300 seconds
    }
    bins[bucket] += 1;
  });

  const maxCount = Math.max(...bins, 1); // Avoid division by zero

  // Generate X-axis labels at every 30 seconds and 60 seconds
  const xLabels = bins.map((count, index) => {
    const time = index * bucketSize;
    if(time === 0) {
      return '0:00';
    }
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;
    if(time % 30 === 0) { // Labels at every 30 seconds
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }
    return '';
  });

  // Determine user's bucket for highlighting
  let userBucket = Math.floor(totalTime / bucketSize);
  if(userBucket >= numBuckets){
    userBucket = numBuckets;
  }

  // Calculate user's rank and percentile
  const sortedPerformanceData = useMemo(() => {
    return [...performanceData].sort((a, b) => a.completion_time - b.completion_time);
  }, [performanceData]);

  const userIndexSorted = useMemo(() => {
    // Rows carry is_you rather than ids; see getPuzzlePerformances.
    return sortedPerformanceData.findIndex(p => p.is_you);
  }, [sortedPerformanceData]);

  const userPosition = useMemo(() => {
    return userIndexSorted !== -1 ? (userIndexSorted + 1) : 'N/A';
  }, [userIndexSorted]);

  const totalUsers = sortedPerformanceData.length;

  // The share artifact. See utils/shareText.js for why it looks like this.
  const shareText = useMemo(() => {
    if (!grid) return '';
    return buildShareText({
      puzzleId,
      totalTime,
      position: userPosition,
      totalPlayers: totalUsers,
      grid,
      assistedKeys,
      assistCount,
      isArchive,
    });
  }, [grid, puzzleId, totalTime, userPosition, totalUsers, assistedKeys, assistCount, isArchive]);

  const handleShare = async () => {
    // The URL is inside `shareText` rather than passed as `url`. iOS Safari
    // frequently drops `text` when both are supplied, which barely mattered
    // for the old two-line string and would silently discard the entire grid.
    if (navigator.share) {
      try {
        await navigator.share({ text: shareText });
        return;
      } catch (error) {
        if (error?.name === 'AbortError') return; // user dismissed the sheet
        // otherwise fall through to the clipboard
      }
    }

    navigator.clipboard.writeText(shareText)
      .then(() => {
        setSnackbarMessage('Copied — paste it anywhere.');
        setSnackbarSeverity('success');
        setOpen(true);
      })
      .catch(() => {
        setSnackbarMessage('Could not copy the share text.');
        setSnackbarSeverity('error');
        setOpen(true);
      });
  };

  let percentFaster = null;
  if(userPosition !== 'N/A' && userPosition < totalUsers && totalUsers >1){
    percentFaster = ((totalUsers - userPosition) / (totalUsers -1)) * 100;
  }

  // Create a reference to the user's leaderboard item
  const userItemRef = useRef(null);

  // Scroll the user's item into view when the leaderboard is rendered
  useEffect(() => {
    if (isComplete && performanceData.length <= 50 && userItemRef.current) {
      userItemRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isComplete, performanceData]);

  const handleRename = async () => {
    if (!onRename || isRenaming) return;
    setIsRenaming(true);
    try {
      const result = await onRename();
      if (result?.retryAfterSeconds) {
        const hours = Math.ceil(result.retryAfterSeconds / 3600);
        setSnackbarMessage(`You can change your name again in ${hours} ${hours === 1 ? 'hour' : 'hours'}.`);
        setSnackbarSeverity('error');
        setOpen(true);
      } else if (!result?.displayName) {
        throw new Error('rename failed');
      }
    } catch {
      setSnackbarMessage('Could not change your name. Try again later.');
      setSnackbarSeverity('error');
      setOpen(true);
    } finally {
      setIsRenaming(false);
    }
  };

  const renderFeedbackForm = () => (
    <div className="feedback-form">
      <textarea
        value={feedbackText}
        onChange={(e) => setFeedbackText(e.target.value)}
        placeholder="Share your suggestions, report issues, or general musings..."
        className="feedback-textarea"
      />
      <div className="feedback-buttons">
        <button
          className="feedback-submit"
          onClick={handleFeedbackSubmit}
          disabled={!feedbackText.trim() || isSubmitting}
        >
          {isSubmitting ? 'Submitting...' : 'Submit'}
        </button>
        <button
          className="feedback-cancel"
          onClick={handleFeedbackCancel}
          disabled={isSubmitting}
        >
          Cancel
        </button>
      </div>
    </div>
  );

  // On busy days the full list gives way to a histogram; the podium and the
  // player's own row stay visible so there are still names to beat.
  const podium = useMemo(() => {
    const top = sortedPerformanceData
      .slice(0, PODIUM_SIZE)
      .map((entry, index) => ({ entry, rank: index + 1 }));
    if (userIndexSorted >= PODIUM_SIZE) {
      top.push({ entry: sortedPerformanceData[userIndexSorted], rank: userIndexSorted + 1 });
    }
    return top;
  }, [sortedPerformanceData, userIndexSorted]);

  const renderRow = (entry, rank, { scrollTarget = false } = {}) => (
    <div
      key={`${rank}-${entry.display_name}`}
      className={`leaderboard-item ${entry.is_you ? 'user' : ''}`}
      ref={scrollTarget && entry.is_you ? userItemRef : null}
    >
      <span className="leaderboard-rank">{getOrdinalSuffix(rank)}</span>
      <span className="leaderboard-name">{entry.display_name}</span>
      {/* Rerolling lives on the player's own row so it costs no extra space. */}
      {entry.is_you && onRename && !isArchive && (
        <button
          className="rename-button"
          onClick={handleRename}
          disabled={isRenaming}
          aria-label="Get a different random name"
          title="Get a different random name"
        >
          <AutorenewIcon fontSize="inherit" />
        </button>
      )}
      <span className="leaderboard-time">{formatTime(entry.completion_time)}</span>
    </div>
  );

  return (
    <div className="summary-backside">
      {isComplete ? (
        <>
          <p className="summary-time">{gaveUp ? '—' : formatTime(totalTime)}</p>
          <p className="summary-time-label">
            {gaveUp
              ? 'Revealed — better luck tomorrow'
              : isArchive ? 'Archive puzzle — not ranked' : 'Solved'}
          </p>

          {/* One line of stats rather than a stack of labelled rows. */}
          <div className="summary-stats">
            {!gaveUp && !isArchive && currentStreak > 0 && (
              <span><strong>{currentStreak}</strong> day streak</span>
            )}
            {averageCompletionTime != null && (
              <span>avg <strong>{averageCompletionTime}</strong></span>
            )}
            {assistCount > 0 && (
              <span><strong>{assistCount}</strong> {assistCount === 1 ? 'hint' : 'hints'}</span>
            )}
          </div>
          {userPosition !== 'N/A' ? (
            performanceData.length <= 50 ? (
              // Render Leaderboard
              <>
                <div className="leaderboard-header">
                  <span>Daily Leaderboard</span>
                  <span>Time</span>
                </div>
                <div className="leaderboard">
                  <div className="leaderboard-items">
                    {sortedPerformanceData.slice(0, 50).map((entry, index) =>
                      renderRow(entry, index + 1, { scrollTarget: true })
                    )}
                  </div>
                </div>
              </>
            ) : (
              // Render Histogram
              <>
                {percentFaster !== null && (
                  <p className="performance-percent">
                    This was faster than {percentFaster.toFixed(0)}% of other players.
                  </p>
                )}
                <div className="histogram-container">
                  <div className="histogram-wrapper">
                    <div className="y-axis">
                      <span className="y-tick y-max">{maxCount}</span>
                      <span className="y-tick y-min">0</span>
                    </div>
                    <div className="histogram-area">
                      <div className="histogram">
                        {bins.map((count, index) => {
                          // Define the label for each bucket
                          let label;
                          if (index < numBuckets) {
                            label = `${index * bucketSize}s - ${(index + 1) * bucketSize}s`;
                          } else {
                            label = `> ${maxTime}s`;
                          }

                          const isUserBucket = index === userBucket;

                          return (
                            <div key={index} className="histogram-bar-wrapper">
                              <div
                                className={`histogram-bar ${isUserBucket ? 'user-bar' : ''}`}
                                style={{ height: `${(count / maxCount) * 100}%` }}
                                title={label}
                              >
                                <span className="bar-tooltip">{count}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div className="x-axis">
                        {xLabels.map((label, index) => (
                          <span key={index} className="x-axis-label-item">
                            {label}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="leaderboard leaderboard-podium">
                  <div className="leaderboard-items">
                    {podium.map(({ entry, rank }) => renderRow(entry, rank))}
                  </div>
                </div>
              </>
            )
          ) : (
            !gaveUp && <p>Your performance is not ranked yet.</p>
          )}
        </>
      ) : (
        <div>
          <h2>Game Paused</h2>
          <h2>Flip back to resume</h2>
        </div>
      )}
      {/* Share and feedback share one line; the feedback form opens below it. */}
      <div className="summary-share-row">
        {isComplete && !gaveUp && shareText && <ShareCard onShare={handleShare} />}
        <button
          className="feedback-button"
          onClick={handleFeedbackClick}
        >
          Have Feedback?
        </button>
      </div>
      {showFeedback && renderFeedbackForm()}

      {isComplete && !isArchive && <NextPuzzleCountdown />}

      {isComplete && (
        <SecondaryActions
          canPlayYesterday={canPlayYesterday}
          onPlayYesterday={onPlayYesterday}
          canInstall={canInstall}
          onInstall={onInstall}
          canNotify={canNotify}
          onNotify={onNotify}
        />
      )}

      <div className="action-buttons" style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
        <button className="flip-back-button" onClick={handleFlipBack} aria-label="Back to the puzzle">
          <Rotate90DegreesCcwIcon />
        </button>
      </div>

      {/* Snackbar for feedback */}
      <Snackbar open={open} autoHideDuration={6000} onClose={handleClose}>
        <Alert onClose={handleClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default SummaryBackside;
